import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { ACCOUNT_REALM } from '@core/config/account-realm.token';
import { TokenResponseDto } from '@features/shared/auth/data/dtos/auth.dto';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { ClientPlatform } from '@shared/utils/client-platform.util';
import { Observable } from 'rxjs';
import { PasskeyCredentialJson } from '../../../domain/models/passkey.model';
import {
  PasskeyEnrollmentAuth, PasskeyStepUpProof,
} from '../../../domain/repositories/passkey.repository';
import {
  PasskeyAssertionOptionsRequestDto, PasskeyChallengeDto, PasskeyDto, PasskeyElevationRequestDto,
  PasskeyLoginRequestDto, PasskeyManagementOperation, PasskeyManagementOptionsRequestDto,
  PasskeyRecoveryConfirmRequestDto, PasskeyRecoveryResultDto, PasskeyRecoveryStartRequestDto,
  PasskeyStepUpDto, RegisterPasskeyOptionsRequestDto, RegisterPasskeyRequestDto,
  RemovePasskeyRequestDto, RenamePasskeyRequestDto, SuperAdminElevationDto,
} from '../../dtos/passkey.dto';

function toStepUpDto(proof: PasskeyStepUpProof | undefined): PasskeyStepUpDto | undefined {
  return proof && { ChallengeId: proof.challengeId, Credential: proof.credential };
}

const PASSKEY_ENDPOINTS = API_ENDPOINTS.auth.passkeys;

@Injectable()
export class PasskeyRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly accountRealm = inject(ACCOUNT_REALM);

  public getRegistrationOptions(
    auth: PasskeyEnrollmentAuth = {}, platform: ClientPlatform | null = null,
  ): Observable<PasskeyChallengeDto> {
    const request: RegisterPasskeyOptionsRequestDto = {
      Password: auth.password,
      StepUp: toStepUpDto(auth.stepUp),
      ClientPlatform: platform ?? undefined,
    };
    return this.http.post<PasskeyChallengeDto>(`${this.apiUrl}${PASSKEY_ENDPOINTS.registrationOptions}`, request);
  }

  public getManagementOptions(operation: PasskeyManagementOperation): Observable<PasskeyChallengeDto> {
    const request: PasskeyManagementOptionsRequestDto = { Operation: operation };
    return this.http.post<PasskeyChallengeDto>(`${this.apiUrl}${PASSKEY_ENDPOINTS.managementOptions}`, request);
  }

  // `observe: 'response'` on register and login alone: those two are the responses that may carry
  // an elevation in their headers, and headers are unreachable from the default body-only observe.
  public register(
    challengeId: string, credential: PasskeyCredentialJson, friendlyName: string,
  ): Observable<HttpResponse<PasskeyDto>> {
    const request: RegisterPasskeyRequestDto = {
      ChallengeId: challengeId,
      Credential: credential,
      FriendlyName: friendlyName,
    };
    return this.http.post<PasskeyDto>(
      `${this.apiUrl}${PASSKEY_ENDPOINTS.register}`, request, { observe: 'response' },
    );
  }

  public list(): Observable<readonly PasskeyDto[]> {
    return this.http.get<readonly PasskeyDto[]>(`${this.apiUrl}${PASSKEY_ENDPOINTS.list}`);
  }

  public rename(id: string, friendlyName: string): Observable<void> {
    const request: RenamePasskeyRequestDto = { FriendlyName: friendlyName };
    return this.http.patch<void>(`${this.apiUrl}${PASSKEY_ENDPOINTS.byId(id)}`, request);
  }

  public remove(id: string, stepUp?: PasskeyStepUpProof): Observable<void> {
    const request: RemovePasskeyRequestDto = { StepUp: toStepUpDto(stepUp) };
    return this.http.delete<void>(`${this.apiUrl}${PASSKEY_ENDPOINTS.byId(id)}`, { body: request });
  }

  public requestRecovery(password: string): Observable<void> {
    const request: PasskeyRecoveryStartRequestDto = { Password: password };
    return this.http.post<void>(`${this.apiUrl}${PASSKEY_ENDPOINTS.recoveryRequest}`, request);
  }

  public confirmRecovery(password: string, code: string): Observable<PasskeyRecoveryResultDto> {
    const request: PasskeyRecoveryConfirmRequestDto = { Password: password, Code: code };
    return this.http.post<PasskeyRecoveryResultDto>(
      `${this.apiUrl}${PASSKEY_ENDPOINTS.recoveryConfirm}`, request,
    );
  }

  public getAssertionOptions(username: string | null): Observable<PasskeyChallengeDto> {
    const request: PasskeyAssertionOptionsRequestDto = { Username: username, AccountRealm: this.accountRealm };
    return this.http.post<PasskeyChallengeDto>(`${this.apiUrl}${PASSKEY_ENDPOINTS.assertionOptions}`, request);
  }

  public login(
    challengeId: string, credential: PasskeyCredentialJson, remember: boolean,
  ): Observable<HttpResponse<TokenResponseDto>> {
    const request: PasskeyLoginRequestDto = {
      ChallengeId: challengeId,
      Credential: credential,
      Remember: remember,
      AccountRealm: this.accountRealm,
    };
    return this.http.post<TokenResponseDto>(
      `${this.apiUrl}${PASSKEY_ENDPOINTS.login}`, request, { observe: 'response' },
    );
  }

  public getElevationOptions(): Observable<PasskeyChallengeDto> {
    return this.http.post<PasskeyChallengeDto>(`${this.apiUrl}${PASSKEY_ENDPOINTS.elevationOptions}`, {});
  }

  public elevate(
    challengeId: string, credential: PasskeyCredentialJson,
  ): Observable<SuperAdminElevationDto> {
    const request: PasskeyElevationRequestDto = { ChallengeId: challengeId, Credential: credential };
    return this.http.post<SuperAdminElevationDto>(`${this.apiUrl}${PASSKEY_ENDPOINTS.elevate}`, request);
  }
}
